/* ─────────────────────────────────────────────────────────────
   Formatting utilities for entity detail display.
   ───────────────────────────────────────────────────────────── */

/**
 * Converts "MM-DD" → "31 December", "03-31" → "31 March".
 * Returns "—" for null/empty values.
 */
export function formatFinancialYearEnd(value: string | null | undefined): string {
  if (!value) return '—';
  const parts = value.split('-');
  if (parts.length !== 2) return value;
  const monthNum = parseInt(parts[0], 10);
  const dayNum = parseInt(parts[1], 10);
  if (isNaN(monthNum) || isNaN(dayNum)) return value;
  try {
    // Use a fixed leap year so Feb 29 works
    const date = new Date(2000, monthNum - 1, dayNum);
    const monthName = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(date);
    return `${dayNum} ${monthName}`;
  } catch {
    return value;
  }
}

/**
 * Converts ISO 3166-1 alpha-2 code → full country name.
 * Returns the code itself as fallback.
 * e.g. "GB" → "United Kingdom"
 */
export function formatCountryName(isoCode: string | null | undefined): string {
  if (!isoCode) return '';
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return displayNames.of(isoCode) ?? isoCode;
  } catch {
    return isoCode;
  }
}

/**
 * Converts ISO 4217 currency code → "CODE — Currency Name".
 * Returns the code itself as fallback.
 * e.g. "GBP" → "GBP — British Pound Sterling"
 */
export function formatCurrencyLabel(code: string | null | undefined): string {
  if (!code) return '';
  try {
    const formatted = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'name',
    }).format(0);
    // Extract the currency name from "0.00 British pounds" → "British pounds"
    // Then capitalise each word
    const nameMatch = formatted.match(/[\d.,\s]+(.+)$/);
    if (nameMatch) {
      const name = nameMatch[1].trim();
      // Title-case the name
      const titled = name.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\b(Of|The|And|In)\b/g, (w) => w.toLowerCase());
      return `${code} — ${titled}`;
    }
    return code;
  } catch {
    return code;
  }
}

/**
 * Formats a percentage to 2 decimal places.
 * e.g. 45 → "45.00%", "45.0" → "45.00%"
 */
export function formatPct(value: string | number | null | undefined): string {
  if (value == null) return '-';
  return `${parseFloat(String(value)).toFixed(2)}%`;
}

/**
 * Formats an ISO date/datetime (or epoch-millis string) → a human-readable day, e.g.
 * "2026-07-31T00:00:00.000Z" → "31 Jul 2026". Returns "—" for null/empty and the raw
 * value back for anything unparseable (never throws).
 *
 * Rendered in UTC on purpose: invoice period / issue / due are calendar (business) dates stored
 * as midnight-UTC, so a viewer behind UTC must NOT see them shift back a day.
 */
export function formatDay(value: string | null | undefined): string {
  if (!value) return '—';
  const raw: string | number = /^\d+$/.test(value) ? Number(value) : value;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 1) return `${diffDays}d ago`
  if (diffMonths < 12) return `${diffMonths}mo ago`
  return `${Math.floor(diffMonths / 12)}yr ago`
}

const ACRONYMS_TO_RESTORE = ['CEO', 'CFO', 'COO', 'CTO', 'CIO', 'MD', 'VP', 'EVP', 'SVP', 'HR', 'IT'];

export function formatRole(role: string): string {
  const titleCased = role
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\B\w+/g, w => w.toLowerCase())
    .replace(/\bAnd\b/g, '&');
  return ACRONYMS_TO_RESTORE.reduce((str, acronym) => {
    const pattern = new RegExp(`\\b${acronym[0]}${acronym.slice(1).toLowerCase()}\\b`, 'g');
    return str.replace(pattern, acronym);
  }, titleCased);
}

/**
 * Human label for a raw service-scope token (`entity-setup` → `Entity setup`).
 * Used by the standardised `ScopeChips` + scope captions across the platform.
 */
export function scopeLabel(token: string): string {
  const spaced = token.replace(/[-_]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---------------------------------------------------------------------------
// Dates and entity types (W3.T3.3)
// ---------------------------------------------------------------------------

/**
 * ONE date formatter, because there were twenty and they disagreed.
 *
 * The contract, chosen from what the codebase already did rather than invented:
 *   locale    en-GB        — 17 of the 18 copies used it
 *   format    dd MMM yyyy  — the dominant shape
 *   fallback  em dash      — matches CellDate, the canonical table cell
 *
 * The one outlier was dashboard/ThingsToDo, rendering en-US dates with the YEAR OMITTED
 * ("Apr 2") next to ComplianceHealthCard's en-GB dates with years, on the same dashboard. In a
 * compliance product a deadline's year is load-bearing, so that was a defect, not a variation.
 *
 * Epoch-millis handling comes from CellDate's implementation, which is the only copy that had it:
 * `new Date("1158796800000")` is Invalid Date, but the backend does send millis-as-string.
 */
/**
 * Parse anything the backend or a caller might hand us into a Date, or null if it is not one.
 *
 * The epoch-millis branch matters: the backend sends millis as a STRING, and
 * `new Date("1158796800000")` is Invalid Date. CellDate was the only one of the twenty copies
 * that handled it.
 */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null || value === '') return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  const raw = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value
  const d = new Date(raw)
  return isNaN(d.getTime()) ? null : d
}

export function formatDate(value: string | number | Date | null | undefined, fallback = '\u2014'): string {
  const d = toDate(value)
  if (!d) return fallback
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Same contract as formatDate, plus 24-hour time. */
export function formatDateTime(value: string | number | Date | null | undefined, fallback = '\u2014'): string {
  const d = toDate(value)
  if (!d) return fallback
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
}

/**
 * Entity-type enum to display label: LEGAL_ENTITY -> "Legal Entity".
 *
 * Six copies existed and their acronym handling disagreed, so the same entity read "LTD" in the
 * registry and "Ltd" in its own detail header. This keeps known acronyms upper-cased and
 * title-cases everything else.
 *
 * Splits on BOTH `_` and `.`: two of the six copies handled dotted values and four did not,
 * which is why the filter dropdown showed "Legalentity" where the column showed "Legal Entity".
 */
const ENTITY_TYPE_ACRONYMS = new Set([
  'LTD', 'LLP', 'LLC', 'PLC', 'LP', 'INC', 'PTE', 'SA', 'NV', 'BV', 'GMBH', 'AG', 'KG', 'SRL', 'SAS', 'REIT',
])

export function formatEntityType(type: string | null | undefined, fallback = '—'): string {
  if (!type) return fallback
  return type
    .split(/[_.]+/)
    .filter(Boolean)
    .map((w) => (ENTITY_TYPE_ACRONYMS.has(w.toUpperCase())
      ? w.toUpperCase()
      : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')
}

// ---------------------------------------------------------------------------
// Numbers and money (W3.T3.4)
// ---------------------------------------------------------------------------

/**
 * Thousands-separated integer/decimal. Five call sites formatted share counts with
 * `new Intl.NumberFormat('en-GB')` inline; this is that, once.
 */
export function formatNumber(
  value: number | string | null | undefined,
  fallback = '—',
): string {
  if (value == null || value === '') return fallback
  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num)) return fallback
  return new Intl.NumberFormat('en-GB').format(num)
}

export interface FormatCurrencyOptions {
  /** Defaults to the VIEWER's locale, which is what 3 of the 4 call sites wanted. */
  locale?: string
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  fallback?: string
}

/**
 * Money, with one implementation of the try/catch every call site had written for itself.
 *
 * `Intl.NumberFormat` THROWS on an unrecognised currency code, and the backend can hand us one.
 * All four previous copies knew this and each invented its own recovery ("CUR 12,345", "12,345
 * CUR", a plain number, the raw input) — they disagreed even on the order.
 *
 * Settled on `12,345 CODE`, amount first, because invoice-utils' behaviour was the only one
 * pinned by a test (invoice-utils.test.ts:163). Picking the other order would have silently
 * changed tested behaviour to match an untested sibling.
 *
 * The options exist because the four call sites genuinely differed and flattening them would be
 * a behaviour change, not a consolidation:
 *   share prices need 2-4 digits (ShareClassesSection)
 *   a KPI tile wants 0 (DiscoverySyncDialog, deliberately rounded)
 *   invoices and synced records take the currency's own default
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  options: FormatCurrencyOptions = {},
): string {
  const { locale, minimumFractionDigits, maximumFractionDigits, fallback = '—' } = options
  if (amount == null || amount === '') return fallback
  const num = typeof amount === 'number' ? amount : Number(amount)
  if (!Number.isFinite(num)) return fallback
  if (!currency) {
    return new Intl.NumberFormat(locale, { minimumFractionDigits, maximumFractionDigits }).format(num)
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(num)
  } catch {
    // Unknown currency code — degrade rather than throwing out of a table row.
    return `${new Intl.NumberFormat(locale, { minimumFractionDigits, maximumFractionDigits }).format(num)} ${currency}`
  }
}
